import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Card from '@/components/Card/Card';
import CardBody from '@/components/Card/CardBody';
import CardHeader from '@/components/Card/CardHeader';
import GridContainer from '@/components/Grid/GridContainer';
import GridItem from '@/components/Grid/GridItem';
import loader from '../../assets/img/loader.svg';
import {
  getOrganization,
  updateOrganization,
} from '@/views/Organizations/Organizations.api';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import NaupaInfoTab from './UpdateOrganizationTabs/NaupaInfoTab';
import DomainsTab from './UpdateOrganizationTabs/DomainsTab';
import PaymentRailsTab from './UpdateOrganizationTabs/PaymentRailsTab';
import FacilitiesTab from './UpdateOrganizationTabs/FacilitiesTab';
import CheckManagementTab from './UpdateOrganizationTabs/CheckManagementTab';
import GeneralTab from './UpdateOrganizationTabs/GeneralTab';
import TangoTab from './UpdateOrganizationTabs/TangoTab';
import ClientBankAccountsTab from './UpdateOrganizationTabs/ClientBankAccountsTab';
import MoovTab from './UpdateOrganizationTabs/MoovTab';
import SftpConnectionsTab from './UpdateOrganizationTabs/SftpConnectionsTab';
import FacilityGroupsTab from './UpdateOrganizationTabs/FacilityGroupsTab';
import WellsFargoSettingsTab from './UpdateOrganizationTabs/WellsFargoSettingsTab';
import { IconButton, Box } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import CustomPaymentTypes from '@/views/Admin/CustomPayments/CustomPaymentTypes';
import PaymentRailSettings from '@/views/PaymentRailSettings/PaymentRailSettings';

const TAB_NAMES = {
  GENERAL: 'general',
  CUSTOM_PAYMENT_TYPES: 'custom-payment-types',
  PAYMENT_RAIL_SETTINGS: 'payment-rail-settings',
  NAUPA_INFO: 'naupa-info',
  DOMAINS: 'domains',
  PAYMENT_RAILS: 'payment-rails',
  FACILITY_GROUPS: 'facility-groups',
  FACILITIES: 'facilities',
  CHECK_MANAGEMENT: 'check-management',
  TANGO: 'tango',
  CLIENT_BANK_ACCOUNTS: 'client-bank-accounts',
  MOOV: 'moov',
  SFTP_CONNECTIONS: 'sftp-connections',
  WELLS_FARGO_SETTINGS: 'wells-fargo-settings',
};

const UpdateOrganization = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [organization, setOrganization] = useState({});

  const [message, setMessage] = useState('');

  const initialTab = window.location.hash.replace('#', '') || TAB_NAMES.GENERAL;
  const [activeTab, setActiveTab] = useState(initialTab);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const { id } = useParams();

  useEffect(() => {
    if (activeTab) {
      window.location.hash = activeTab;
    }
  }, [activeTab]);

  useEffect(() => {
    const onHashChange = () => {
      setActiveTab(window.location.hash.replace('#', '') || TAB_NAMES.GENERAL);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await getOrganizationData(id);
      } catch (error) {
        console.error(error);
      }
    };

    fetchData();
  }, [id]);

  const getOrganizationData = async (orgId) => {
    try {
      const res = await getOrganization(orgId);
      setOrganization(res.data.organization);
      setLoading(false);
    } catch (error) {
      console.error(error);
    }
  };

  const handleUpdateOrganization = async (formData) => {
    if (!organization.name) {
      alert(t('organizations.update.validation.nameRequired'));
      return;
    }

    setLoading(true);
    try {
      const res = await updateOrganization(id, formData);
      if (!res.data?.err) {
        await getOrganizationData(id);
      } else {
        setMessage(res.data?.msg);
        alert(res.data?.msg);
      }
    } catch (err) {
      setMessage(err.response?.data?.msg || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleManagedAccountMembershipUpdated = (managedAccountId) => {
    setOrganization((current) => ({
      ...current,
      managed_account_id: managedAccountId,
    }));
  };

  const handleBackClick = () => {
    navigate('/admin/organizations');
  };

  const renderTabContent = () => {
    if (loading)
      return (
        <div className='loaders' style={{ marginTop: '20px' }}>
          <img alt={'loader'} src={loader} />
        </div>
      );
    return (
      <>
        {activeTab === TAB_NAMES.GENERAL && (
          <GeneralTab
            organization={organization}
            updateOrganization={handleUpdateOrganization}
            onManagedAccountMembershipUpdated={
              handleManagedAccountMembershipUpdated
            }
          />
        )}

        {activeTab === TAB_NAMES.CUSTOM_PAYMENT_TYPES && (
          <CustomPaymentTypes organizationId={organization.id} />
        )}

        {activeTab === TAB_NAMES.PAYMENT_RAIL_SETTINGS && (
          <PaymentRailSettings organizationId={organization.id} />
        )}

        {activeTab === TAB_NAMES.NAUPA_INFO && (
          <NaupaInfoTab
            organization={organization}
            updateOrganization={handleUpdateOrganization}
          />
        )}

        {activeTab === TAB_NAMES.DOMAINS && (
          <DomainsTab organizationId={organization.id} />
        )}

        {activeTab === TAB_NAMES.PAYMENT_RAILS && (
          <PaymentRailsTab organizationId={organization.id} />
        )}

        {activeTab === TAB_NAMES.FACILITY_GROUPS && (
          <FacilityGroupsTab organizationId={organization.id} />
        )}

        {activeTab === TAB_NAMES.FACILITIES && (
          <FacilitiesTab organizationId={organization.id} />
        )}

        {activeTab === TAB_NAMES.CHECK_MANAGEMENT && (
          <CheckManagementTab organizationId={organization.id} />
        )}

        {activeTab === TAB_NAMES.TANGO && (
          <TangoTab
            organization={organization}
            updateOrganization={handleUpdateOrganization}
          />
        )}

        {activeTab === TAB_NAMES.CLIENT_BANK_ACCOUNTS && (
          <ClientBankAccountsTab organizationId={organization.id} />
        )}

        {activeTab === TAB_NAMES.MOOV && (
          <MoovTab organizationId={organization.id} />
        )}

        {activeTab === TAB_NAMES.SFTP_CONNECTIONS && (
          <SftpConnectionsTab organizationId={organization.id} />
        )}

        {activeTab === TAB_NAMES.WELLS_FARGO_SETTINGS && (
          <WellsFargoSettingsTab
            organizationId={organization.id}
            isManaged={organization.managed_account_id != null}
          />
        )}
      </>
    );
  };

  return (
    <div>
      <form onSubmit={updateOrganization}>
        <GridContainer>
          <GridItem xs={12} sm={12} md={12}>
            <Card>
              <CardHeader color='primary'>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton
                    onClick={handleBackClick}
                    sx={{
                      color: 'white !important',
                      padding: '4px',
                      '& .MuiSvgIcon-root': {
                        color: 'white !important',
                      },
                    }}
                    size='small'>
                    <ArrowBack sx={{ color: 'white !important' }} />
                  </IconButton>
                  <h4>{t('organizations.update.title')}</h4>
                </Box>
              </CardHeader>
              <CardBody>
                <Tabs
                  style={{ paddingTop: '30px' }}
                  variant='scrollable'
                  value={activeTab}
                  onChange={handleTabChange}>
                  <Tab
                    value={TAB_NAMES.GENERAL}
                    label={t('organizations.update.generalSection')}
                  />
                  <Tab
                    value={TAB_NAMES.CUSTOM_PAYMENT_TYPES}
                    label='Custom Payment Types'
                  />
                  <Tab
                    value={TAB_NAMES.PAYMENT_RAIL_SETTINGS}
                    label={t('paymentRailSettings.title')}
                  />
                  <Tab
                    value={TAB_NAMES.NAUPA_INFO}
                    label={t('organizations.update.naupaInfoSection')}
                  />
                  <Tab
                    value={TAB_NAMES.DOMAINS}
                    label={t('organizations.update.domainsSection')}
                  />
                  <Tab
                    value={TAB_NAMES.PAYMENT_RAILS}
                    label={t('organizations.update.paymentRailsSection')}
                  />
                  <Tab
                    value={TAB_NAMES.FACILITY_GROUPS}
                    label={t('organizations.update.facilityGroupsSection')}
                  />
                  <Tab
                    value={TAB_NAMES.FACILITIES}
                    label={t('organizations.update.facilitiesSection')}
                  />
                  <Tab
                    value={TAB_NAMES.CHECK_MANAGEMENT}
                    label={t('organizations.update.checkManagementSection')}
                  />
                  <Tab
                    value={TAB_NAMES.TANGO}
                    label={t('organizations.update.tangoSection')}
                  />
                  <Tab
                    value={TAB_NAMES.CLIENT_BANK_ACCOUNTS}
                    label={t('organizations.update.clientBankAccountsSection')}
                  />
                  <Tab
                    value={TAB_NAMES.MOOV}
                    label={t('organizations.update.moovSection')}
                  />
                  <Tab
                    value={TAB_NAMES.SFTP_CONNECTIONS}
                    label={t('organizations.update.sftpConnectionsSection')}
                  />
                  <Tab
                    value={TAB_NAMES.WELLS_FARGO_SETTINGS}
                    label={t('organizations.update.wellsFargoSettingsSection')}
                  />
                </Tabs>
                {renderTabContent()}
                {message && (
                  <GridContainer>
                    <GridItem xs={8} sm={8} md={6}>
                      <p style={{ color: 'red', padding: '10px 0' }}>
                        {message}
                      </p>
                    </GridItem>
                  </GridContainer>
                )}
              </CardBody>
            </Card>
          </GridItem>
        </GridContainer>
      </form>
    </div>
  );
};

export default UpdateOrganization;
